import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion'
import './index.scss'

const PROMPT = 'pius@ver-it:~$'
const COMMAND = 'whoami'

const START_DELAY = 900
const COMMAND_TYPE_DELAY = 120
const ENTER_DELAY = 500
const OUTPUT_TYPE_DELAY = 35
const PROMPT_DELAY = 400

// The animation advances one step at a time: steps 0..COMMAND.length type the
// command, the next steps type the output one character each, and the last
// step shows the new prompt.
const OUTPUT_START = COMMAND.length + 1

const getDelay = (step, outputEnd) => {
  if (step === 0) return START_DELAY
  if (step < COMMAND.length) return COMMAND_TYPE_DELAY
  if (step === COMMAND.length) return ENTER_DELAY
  if (step < outputEnd) return OUTPUT_TYPE_DELAY
  return PROMPT_DELAY
}

const Cursor = ({ typing }) => (
  <span className={`terminal-cursor${typing ? ' is-typing' : ''}`} />
)

/**
 * Small terminal that types `whoami` and then the translated
 * `terminal_output` once. Text that is not typed yet keeps its space, so the
 * box and the line breaks do not change while typing. With reduced motion
 * the final state is shown right away. When the language changes, typing
 * continues with the new text from the same position.
 *
 * @param {object} props
 * @param {string} [props.className] - Extra class names for the outer element.
 * @param {Function} [props.onComplete] - Called once when the new prompt is shown.
 */
const Terminal = ({ className = '', onComplete }) => {
  const { t } = useTranslation()
  const reducedMotion = usePrefersReducedMotion()
  const [step, setStep] = useState(0)
  const onCompleteRef = useRef(onComplete)

  const output = t('terminal_output')
  const outputEnd = OUTPUT_START + output.length
  const finalStep = outputEnd + 1

  useEffect(() => {
    if (reducedMotion || step >= finalStep) return undefined
    // Jumping to Infinity at the end keeps the terminal finished even if a
    // language change makes the output longer afterwards.
    const advance = (current) => (current + 1 >= finalStep ? Infinity : current + 1)
    const timeout = setTimeout(() => setStep(advance), getDelay(step, outputEnd))
    return () => clearTimeout(timeout)
  }, [step, reducedMotion, outputEnd, finalStep])

  const currentStep = reducedMotion ? finalStep : Math.min(step, finalStep)
  const isComplete = currentStep >= finalStep

  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  useEffect(() => {
    if (isComplete) onCompleteRef.current?.()
  }, [isComplete])
  const typedOutputLength = Math.max(0, Math.min(currentStep - OUTPUT_START, output.length))
  const commandTyping = currentStep > 0 && currentStep < COMMAND.length
  const outputTyping = currentStep >= OUTPUT_START && currentStep < outputEnd

  return (
    <div className={`terminal ${className}`.trim()}>
      <span className="sr-only">
        {`${PROMPT} ${COMMAND}`}
        <br />
        {output}
      </span>
      <div className="terminal-screen" aria-hidden="true">
        <div className="terminal-line">
          <span className="terminal-prompt">{PROMPT}</span>{' '}
          <span className="terminal-command">{COMMAND.slice(0, currentStep)}</span>
          {currentStep < OUTPUT_START && <Cursor typing={commandTyping} />}
        </div>
        <div className="terminal-line terminal-output">
          {output.slice(0, typedOutputLength)}
          {currentStep >= OUTPUT_START && currentStep < finalStep && <Cursor typing={outputTyping} />}
          <span className="terminal-pending">{output.slice(typedOutputLength)}</span>
        </div>
        <div className={`terminal-line${currentStep < finalStep ? ' is-pending' : ''}`}>
          <span className="terminal-prompt">{PROMPT}</span>{' '}
          <Cursor typing={false} />
        </div>
      </div>
    </div>
  )
}

export default Terminal
